import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, Phase } from "../lib/api";
import { useAuthStore } from "../store/auth";
import {
  CheckCircleIcon,
  LockClosedIcon,
  PlayCircleIcon,
  ChartBarIcon,
  EyeIcon,
  DocumentTextIcon,
  SparklesIcon,
  QuestionMarkCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  BookOpenIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";
import ComplianceDocsCard from "../components/ComplianceDocsCard";

const openCoachChat = () => window.dispatchEvent(new CustomEvent('open-coach-chat'));

export default function DashboardPage() {
  const { user } = useAuthStore();
  const [phases, setPhases] = useState<Phase[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showGuide, setShowGuide] = useState(() => {
    return localStorage.getItem('guide_collapsed') !== 'true';
  });

  const toggleGuide = () => {
    const next = !showGuide;
    setShowGuide(next);
    if (!next) localStorage.setItem('guide_collapsed', 'true');
    else localStorage.removeItem('guide_collapsed');
  };

  const hideGuide = () => {
    setShowGuide(false);
    localStorage.setItem('guide_collapsed', 'true');
  };

  useEffect(() => {
    loadPhases();
  }, []);

  const loadPhases = async () => {
    try {
      const response = await api.listPhases();
      setPhases(response.phases);
    } catch (error) {
      console.error("Failed to load phases:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-copper-500"></div>
      </div>
    );
  }

  const completedCount = phases.filter((p) => p.status === "completed").length;
  const progress = Math.round((completedCount / phases.length) * 100);

  const currentPhase = phases.find(p => p.status === 'available' || p.status === 'pending_review') || phases[phases.length - 1];
  const reviewCount = phases.filter(p => p.status === 'pending_review').length;

  return (
    <div className="space-y-8">
      {/* Welcome Header with Stats */}
      <div className="relative overflow-hidden rounded-2xl bg-white border border-charcoal-100 shadow-sm">
        {/* Decorative background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -right-20 w-64 h-64 bg-[radial-gradient(circle,rgba(196,117,76,0.06),transparent_70%)]" />
          <div className="absolute -bottom-16 -left-16 w-48 h-48 bg-[radial-gradient(circle,rgba(90,123,111,0.05),transparent_70%)]" />
          <svg className="absolute right-0 top-0 w-48 h-48 opacity-[0.03]" viewBox="0 0 200 200">
            <circle cx="150" cy="50" r="80" fill="none" stroke="#2C3E3D" strokeWidth="1" />
            <circle cx="150" cy="50" r="60" fill="none" stroke="#2C3E3D" strokeWidth="0.7" />
            <circle cx="150" cy="50" r="40" fill="none" stroke="#2C3E3D" strokeWidth="0.5" />
          </svg>
        </div>

        <div className="h-1 bg-gradient-to-r from-copper-400 via-sage-400 to-teal-400" />

        <div className="relative p-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-charcoal-500">
                Welcome back, {user?.business_name}!
              </h1>
              <p className="text-charcoal-300 mt-2">
                {progress === 100
                  ? "Your marketing system is complete! Head to Activation to launch."
                  : `Your marketing system is ${progress}% complete`}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={openCoachChat}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-charcoal-400 hover:text-copper-500 hover:bg-copper-50 rounded-lg border border-charcoal-100 hover:border-copper-200 transition-colors"
              >
                <QuestionMarkCircleIcon className="h-4 w-4" />
                <span>Get Help</span>
              </button>
              <div className="flex items-center space-x-2 text-copper-500">
                <ChartBarIcon className="h-8 w-8" />
                <span className="text-3xl font-bold">
                  {completedCount}/{phases.length}
                </span>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-6">
            <div className="w-full bg-charcoal-100 rounded-full h-3 overflow-hidden">
              <div
                className="bg-gradient-to-r from-copper-400 via-copper-500 to-sage-500 h-3 rounded-full transition-all duration-500 relative"
                style={{ width: `${progress}%` }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-[shimmer_2s_infinite] rounded-full" />
              </div>
            </div>
          </div>

          {/* Quick Stats Row */}
          <div className="mt-6 grid grid-cols-3 gap-4">
            <div className="text-center p-3 rounded-xl bg-sage-50 border border-sage-100">
              <p className="text-2xl font-bold text-sage-600">{completedCount}</p>
              <p className="text-xs text-charcoal-300 mt-1">Phases Done</p>
            </div>
            <div className="text-center p-3 rounded-xl bg-copper-50 border border-copper-100">
              <p className="text-2xl font-bold text-copper-500">{reviewCount}</p>
              <p className="text-xs text-charcoal-300 mt-1">Ready to Review</p>
            </div>
            <div className="text-center p-3 rounded-xl bg-teal-50 border border-teal-100">
              <p className="text-2xl font-bold text-teal-600">{currentPhase?.number || '-'}</p>
              <p className="text-xs text-charcoal-300 mt-1">Current Phase</p>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works Guide */}
      <div className="rounded-2xl bg-white border border-charcoal-100 shadow-sm overflow-hidden">
        <button
          onClick={toggleGuide}
          className="w-full flex items-center justify-between p-6 text-left hover:bg-charcoal-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-copper-50 rounded-xl">
              <BookOpenIcon className="h-5 w-5 text-copper-500" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-charcoal-500">How It Works</h2>
              <p className="text-sm text-charcoal-300">Learn how to get the most out of Trailhead</p>
            </div>
          </div>
          {showGuide
            ? <ChevronUpIcon className="h-5 w-5 text-charcoal-300 flex-shrink-0" />
            : <ChevronDownIcon className="h-5 w-5 text-charcoal-300 flex-shrink-0" />
          }
        </button>

        {showGuide && (
          <div className="border-t border-charcoal-100">
            {/* Video placeholder — swap src once you have an embed URL */}
            <div className="aspect-video w-full">
              <iframe
                src="https://www.youtube.com/embed/9XtOe3ubhTI"
                className="w-full h-full"
                allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                title="How Trailhead Works"
              />
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left: Overview */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-charcoal-500">Your complete marketing system, built in 10 phases.</h3>
                  <p className="text-sm text-charcoal-400 leading-relaxed">
                    Each phase is handled by our dedicated team of Rangers. They know the trail ahead quite well and will make sure you have everything
                    you need to enjoy every step of the adventure ahead.
                  </p>
                  <p className="text-sm text-charcoal-400 leading-relaxed">
                    By the end of this journey, you will have robust business intelligence to help you make
                    better decisions, strong messaging to attract the right clients, a detailed and fully
                    personalized marketing program, and plenty of your own time left to actually make your
                    business grow.
                  </p>
                </div>

                {/* Right: Steps */}
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center bg-copper-100 text-copper-600">1</div>
                    <div>
                      <p className="text-sm font-semibold text-charcoal-500">The Rangers get to work</p>
                      <p className="text-sm text-charcoal-300 leading-snug mt-0.5">They research your business, scout your competitors, and produce each deliverable. Most phases are ready within minutes.</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center bg-sage-100 text-sage-600">2</div>
                    <div>
                      <p className="text-sm font-semibold text-charcoal-500">You review</p>
                      <p className="text-sm text-charcoal-300 leading-snug mt-0.5">When a phase is ready, it shows up here as "Ready to Review." Read through your materials at your own pace.</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center bg-teal-100 text-teal-600">3</div>
                    <div>
                      <p className="text-sm font-semibold text-charcoal-500">Request changes</p>
                      <p className="text-sm text-charcoal-300 leading-snug mt-0.5">Not quite right? Request a revision and the Rangers will update it. You have multiple revision rounds per phase.</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center bg-copper-100 text-copper-600">4</div>
                    <div>
                      <p className="text-sm font-semibold text-charcoal-500">Approve and move on</p>
                      <p className="text-sm text-charcoal-300 leading-snug mt-0.5">Once you approve, that phase is locked in and the next one unlocks automatically.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Coach Chat CTA */}
              <div className="mt-6 p-5 bg-gradient-to-r from-charcoal-700 to-charcoal-800 rounded-2xl flex items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <ChatBubbleLeftRightIcon className="h-6 w-6 text-copper-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-white text-sm">Your coach is always on the trail with you.</p>
                    <p className="text-copper-200 text-sm mt-0.5">Questions about a phase? Not sure what to approve? Just ask — your coach will reply directly in the chat.</p>
                  </div>
                </div>
                <button
                  onClick={openCoachChat}
                  className="flex-shrink-0 px-4 py-2 bg-copper-500 hover:bg-copper-600 text-white text-sm font-semibold rounded-xl transition-colors shadow-md shadow-copper-900/30 whitespace-nowrap"
                >
                  Open Chat
                </button>
              </div>

              {/* Hide button */}
              <div className="mt-6 flex justify-center">
                <button
                  onClick={hideGuide}
                  className="px-6 py-2.5 text-sm font-medium text-charcoal-300 hover:text-charcoal-500 bg-charcoal-50 hover:bg-charcoal-100 rounded-xl border border-charcoal-100 transition-colors"
                >
                  Hide this for now
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Journey Timeline */}
      <div className="relative overflow-hidden rounded-2xl bg-white border border-charcoal-100 shadow-sm">
        <div className="absolute inset-0 pointer-events-none">
          {/* Mountain silhouette: rises left-to-right, peaks at Activation (far right) */}
          <svg className="absolute left-0 bottom-0 w-full h-full opacity-[0.045]" viewBox="0 0 1200 140" preserveAspectRatio="none">
            <path d="M0,120 L100,105 L200,95 L350,85 L500,70 L650,55 L780,38 L900,22 L980,10 L1050,4 L1120,8 L1200,15 L1200,140 L0,140 Z" fill="#5A7B6F" />
            <path d="M0,135 L120,122 L250,112 L400,100 L550,88 L700,72 L820,55 L920,38 L1010,20 L1080,8 L1130,2 L1170,5 L1200,12 L1200,140 L0,140 Z" fill="#2C3E3D" />
            <path d="M0,140 L0,138 L150,130 L350,120 L550,108 L720,94 L880,76 L1000,56 L1100,35 L1160,18 L1185,8 L1200,4 L1200,140 Z" fill="#C4754C" opacity="0.4" />
          </svg>
        </div>
        <div className="relative p-6 overflow-x-auto">
          <h2 className="text-lg font-bold text-charcoal-500 mb-4">Your Journey to Business Growth</h2>
          <div className="flex items-center min-w-[640px] py-2">
            {phases.map((phase, idx) => {
              const isCompleted = phase.status === 'completed';
              const isCurrent = phase.status === 'available' || phase.status === 'pending_review';
              const isLast = idx === phases.length - 1;
              return (
                <div key={phase.number} className="flex items-center flex-1">
                  <Link
                    to={phase.status !== 'locked' ? `/phases/${phase.slug}` : '#'}
                    className="flex flex-col items-center group relative"
                    title={`Phase ${phase.number}: ${phase.name}`}
                  >
                    <div
                      className={clsx(
                        'w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all',
                        isCompleted && 'bg-sage-500 text-white shadow-sm shadow-sage-500/20',
                        isCurrent && 'bg-copper-500 text-white ring-4 ring-copper-200 animate-pulse shadow-md shadow-copper-500/20',
                        !isCompleted && !isCurrent && 'bg-charcoal-100 text-charcoal-300'
                      )}
                    >
                      {isCompleted ? (
                        <CheckCircleIcon className="h-5 w-5" />
                      ) : (
                        phase.number
                      )}
                    </div>
                    <span
                      className={clsx(
                        'text-xs mt-2 text-center max-w-[80px] leading-tight',
                        isCompleted && 'text-sage-600 font-medium',
                        isCurrent && 'text-copper-600 font-bold',
                        !isCompleted && !isCurrent && 'text-charcoal-300'
                      )}
                    >
                      {phase.name}
                    </span>
                  </Link>
                  {!isLast && (
                    <div
                      className={clsx(
                        'flex-1 h-1 mx-1 rounded-full mt-[-20px] transition-all',
                        isCompleted ? 'bg-gradient-to-r from-sage-400 to-sage-300' : 'bg-charcoal-100'
                      )}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Action Callout */}
      {reviewCount > 0 && (
        <div className="bg-gradient-to-r from-copper-50 to-cream-50 border border-copper-200 rounded-xl p-4 flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-copper-100 rounded-lg">
              <EyeIcon className="h-5 w-5 text-copper-500" />
            </div>
            <p className="text-charcoal-500">
              <span className="font-bold">{reviewCount} phase{reviewCount > 1 ? 's' : ''}</span> ready for your review
            </p>
          </div>
          <Link
            to={`/phases/${phases.find(p => p.status === 'pending_review')?.slug}/review`}
            className="btn btn-primary text-sm"
          >
            Review Now
          </Link>
        </div>
      )}

      {progress === 100 && (
        <div className="bg-gradient-to-r from-sage-50 to-teal-50 border border-sage-200 rounded-xl p-4 flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-sage-100 rounded-lg">
              <SparklesIcon className="h-5 w-5 text-sage-500" />
            </div>
            <p className="text-charcoal-500">
              <span className="font-bold">All phases complete!</span> Your marketing system is ready to launch.
            </p>
          </div>
          <Link to="/activation" className="btn btn-primary text-sm">
            Go to Activation
          </Link>
        </div>
      )}

      {/* Phases Grid */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-charcoal-500">Phase Details</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {phases.map((phase) => (
            <PhaseCard key={phase.number} phase={phase} />
          ))}
        </div>
      </div>

      {/* Compliance Documentation */}
      <ComplianceDocsCard />
    </div>
  );
}

function PhaseCard({ phase }: { phase: Phase }) {
  const statusConfig: Record<
    string,
    {
      icon: typeof CheckCircleIcon;
      iconColor: string;
      bgColor: string;
      borderColor: string;
      badge: string;
      badgeText: string;
      glowColor: string;
    }
  > = {
    completed: {
      icon: CheckCircleIcon,
      iconColor: "text-sage-500",
      bgColor: "bg-sage-50",
      borderColor: "border-sage-400",
      badge: "badge-sage",
      badgeText: "Completed",
      glowColor: "from-sage-400 to-sage-500",
    },
    pending_review: {
      icon: EyeIcon,
      iconColor: "text-copper-500",
      bgColor: "bg-copper-50",
      borderColor: "border-copper-400",
      badge: "badge-copper",
      badgeText: "Ready for Review",
      glowColor: "from-copper-400 to-copper-500",
    },
    available: {
      icon: PlayCircleIcon,
      iconColor: "text-teal-500",
      bgColor: "bg-teal-50",
      borderColor: "border-teal-300",
      badge: "badge-teal",
      badgeText: "Ready",
      glowColor: "from-teal-400 to-teal-500",
    },
    locked: {
      icon: LockClosedIcon,
      iconColor: "text-charcoal-300",
      bgColor: "bg-charcoal-50",
      borderColor: "border-charcoal-200",
      badge: "badge-gray",
      badgeText: "Locked",
      glowColor: "from-charcoal-200 to-charcoal-300",
    },
  };

  const config = statusConfig[phase.status] || statusConfig.locked;
  const Icon = config.icon;
  const hasContent =
    phase.status === "completed" || phase.status === "pending_review";
  const reviewPath =
    phase.slug === "blog"
      ? "/phases/blog/review"
      : `/phases/${phase.slug}/review`;

  return (
    <div
      className={clsx(
        "relative overflow-hidden bg-white rounded-2xl border border-charcoal-100 shadow-sm hover:shadow-md transition-all duration-200 border-l-4 flex flex-col group",
        config.borderColor,
        phase.status === "locked" && "opacity-60",
        (phase.status === "available" || phase.status === "pending_review") &&
          "shadow-[0_0_20px_4px_rgba(196,117,76,0.25)] hover:shadow-[0_0_28px_6px_rgba(196,117,76,0.35)]",
        (phase.status === "available" || phase.status === "pending_review") &&
          "shadow-[0_0_20px_4px_rgba(196,117,76,0.25)] hover:shadow-[0_0_28px_6px_rgba(196,117,76,0.35)]"
      )}
    >
      {phase.status !== 'locked' && (
        <div className="absolute top-0 right-0 w-16 h-16 pointer-events-none opacity-[0.04]">
          <svg viewBox="0 0 64 64" className="w-full h-full">
            <circle cx="64" cy="0" r="48" fill="none" stroke="#2C3E3D" strokeWidth="1" />
            <circle cx="64" cy="0" r="32" fill="none" stroke="#2C3E3D" strokeWidth="0.7" />
          </svg>
        </div>
      )}

      <Link
        to={hasContent ? reviewPath : `/phases/${phase.slug}`}
        className={clsx(
          "block flex-1 p-6",
          phase.status === "locked" &&
            "cursor-not-allowed pointer-events-none"
        )}
      >
        <div className="flex items-start justify-between mb-4">
          <div className={clsx("p-2 rounded-xl", config.bgColor)}>
            <Icon className={clsx("h-6 w-6", config.iconColor)} />
          </div>
          <span className={config.badge}>{config.badgeText}</span>
        </div>

        <h3 className="text-lg font-bold text-charcoal-500 mb-2">
          Phase {phase.number}: {phase.name}
        </h3>

        <p className="text-sm text-charcoal-300 line-clamp-2">
          {phase.description}
        </p>
      </Link>

      {hasContent && (
        <div className="px-6 pb-5 pt-0">
          <div className="pt-4 border-t border-charcoal-100 flex items-center justify-end">
            <Link
              to={reviewPath}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-copper-500 text-white hover:bg-copper-600 transition-colors shadow-sm shadow-copper-500/20"
            >
              <DocumentTextIcon className="h-3.5 w-3.5" />
              View Your Materials
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
